import { apiGet, apiSend } from "../../lib/api";
import type {
  SmsContact,
  SmsContactListType,
  SmsConversation,
  SmsMessage,
  SmsSettings,
} from "./types";

// Thin wrappers around the HQ gateway → hq-sms-worker. All auth is handled
// by `apiGet` / `apiSend` which attach the Supabase access token.

const SMS_READ_MAP_KEY = "hq_sms_read_map";

export async function fetchSmsStats(): Promise<{ unread: number }> {
  let lastSeen: Record<string, string> = {};
  try { lastSeen = JSON.parse(localStorage.getItem(SMS_READ_MAP_KEY) || "{}"); } catch {}
  const convos = await fetchConversations({ lastSeen });
  const unread = convos.reduce((sum, c) => sum + c.unread_count, 0);
  return { unread };
}

export function fetchMessages(params: {
  contact?: string;
  phoneLine?: "line1" | "line2" | null;
  limit?: number;
  after?: string;
  /** Inclusive lower bound (ISO). Used by the Reports view + CallsView SmsIndex. */
  from?: string;
  /** Exclusive upper bound (ISO). Matches /voice/calls bracketing. */
  to?: string;
  /** When true, returns archived/hidden messages too (used by the Log view). */
  includeHidden?: boolean;
}): Promise<SmsMessage[]> {
  const q = new URLSearchParams();
  if (params.contact) q.set("contact", params.contact);
  if (params.phoneLine) q.set("phone_line", params.phoneLine);
  if (params.limit) q.set("limit", String(params.limit));
  if (params.after) q.set("after", params.after);
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  if (params.includeHidden) q.set("include_hidden", "true");
  const qs = q.toString();
  return apiGet<SmsMessage[]>(`/sms/messages${qs ? `?${qs}` : ""}`);
}

export function hideMessage(id: string): Promise<{ success: boolean }> {
  return apiSend(`/sms/messages/${encodeURIComponent(id)}`, "PATCH");
}

export function sendMessage(args: {
  to: string;
  from: string;
  body: string;
}): Promise<SmsMessage> {
  return apiSend<SmsMessage>("/sms/send", "POST", args);
}

// The worker's error codes arrive as `"<status>: {\"error\":\"...\"}"`, which is
// not something an operator should have to read.
export function describeSendError(err: unknown): string {
  const raw = (err as Error)?.message ?? "";
  if (raw.includes("recipient_opted_out")) {
    return "This contact texted STOP and can't be messaged.";
  }
  if (raw.includes("optout_check_unavailable")) {
    return "Can't verify opt-out status right now, so the message wasn't sent. Try again in a moment.";
  }
  if (raw.includes("from_not_owned_line")) {
    return "That isn't one of the Tribal Axe numbers.";
  }
  if (raw.includes("twilio_send_failed")) {
    return "Twilio rejected the message. Check the number and try again.";
  }
  return raw || "Failed to send";
}

export function fetchConversations(args: {
  phoneLine?: "line1" | "line2" | null;
  lastSeen?: Record<string, string>;
}): Promise<SmsConversation[]> {
  const q = new URLSearchParams();
  if (args.phoneLine) q.set("phone_line", args.phoneLine);
  if (args.lastSeen && Object.keys(args.lastSeen).length > 0) {
    q.set("last_seen", JSON.stringify(args.lastSeen));
  }
  const qs = q.toString();
  return apiGet<SmsConversation[]>(`/sms/conversations${qs ? `?${qs}` : ""}`);
}

export function archiveConversation(contact: string): Promise<{ success: boolean }> {
  return apiSend(`/sms/conversations/${encodeURIComponent(contact)}/archive`, "PATCH");
}

export function fetchSettings(): Promise<Partial<SmsSettings>> {
  return apiGet<Partial<SmsSettings>>("/sms/settings");
}

export function saveSettings(
  settings: Partial<SmsSettings>
): Promise<SmsSettings> {
  return apiSend<SmsSettings>("/sms/settings", "PUT", settings);
}

export function fetchContacts(): Promise<SmsContact[]> {
  return apiGet<SmsContact[]>("/sms/contacts");
}

export function createContact(contact: {
  name: string;
  phone_number: string;
  list_type: SmsContactListType;
  notes?: string | null;
}): Promise<SmsContact> {
  return apiSend<SmsContact>("/sms/contacts", "POST", contact);
}

export function updateContact(
  id: string,
  patch: Partial<Omit<SmsContact, "id" | "created_at">>
): Promise<SmsContact> {
  return apiSend<SmsContact>(`/sms/contacts/${encodeURIComponent(id)}`, "PATCH", patch);
}

export function deleteContact(id: string): Promise<{ success: boolean }> {
  return apiSend(`/sms/contacts/${encodeURIComponent(id)}`, "DELETE");
}
